
import React from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, MapPin, Loader2, AlertTriangle, ArrowLeft, Receipt, Star } from "lucide-react";
import { format } from "date-fns";
import { updateOrderStatus } from "@/services/orderService";
import { useToast } from "@/hooks/use-toast";
import BackButton from "@/components/navigation/BackButton";
import { useHasUserReviewed } from "@/hooks/useHasUserReviewed";
import { WriteReviewDialog } from "@/components/ProductDetail/WriteReviewDialog";
import { useState } from "react";

interface ShippingAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

const OrderDetailPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reviewProductId, setReviewProductId] = useState<string | null>(null);
  const [reviewProductName, setReviewProductName] = useState<string>('');

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order-details', orderId],
    queryFn: async () => {
      if (!orderId) return null;
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_id,
            quantity,
            price,
            products (
              id,
              name,
              image_url
            )
          )
        `)
        .eq('id', orderId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!orderId,
  });

  const { mutate: cancelOrder, isPending: isCancelling } = useMutation({
    mutationFn: (id: string) => updateOrderStatus(id, 'cancelled'),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: "Order Cancelled",
          description: "Your order has been successfully cancelled.",
        });
        queryClient.invalidateQueries({ queryKey: ['order-details', orderId] });
        queryClient.invalidateQueries({ queryKey: ['user-orders'] });
        navigate('/orders');
      } else {
        throw new Error(result.error || "Failed to cancel order.");
      }
    },
    onError: (error) => {
      toast({
        title: "Cancellation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCancelOrder = () => {
    if (order) {
      cancelOrder(order.id);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderShippingAddress = (address: any) => {
    if (!address) return <p>No shipping address provided</p>;
    
    if (typeof address === 'string') {
      try {
        const parsed = JSON.parse(address);
        return renderAddressContent(parsed);
      } catch {
        return <p>{address}</p>;
      }
    }
    
    return renderAddressContent(address);
  };

  const renderAddressContent = (addr: ShippingAddress) => {
    return (
      <div>
        {addr.street && <p>{addr.street}</p>}
        {(addr.city || addr.state || addr.zip) && (
          <p>
            {addr.city && addr.city}
            {addr.city && addr.state && ', '}
            {addr.state && addr.state}
            {addr.zip && ` ${addr.zip}`}
          </p>
        )}
        {addr.country && <p>{addr.country}</p>}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-5xl py-8 px-4 sm:px-6 lg:px-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="space-y-6">
          <Card variant="glass">
            <CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent>
          </Card>
          <Card variant="glass">
            <CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent>
          </Card>
          <Card variant="glass">
            <CardContent className="p-6"><Skeleton className="h-24 w-full" /></CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="container mx-auto max-w-5xl py-8 px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Order Not Found</h2>
        <p className="text-muted-foreground mb-6">We couldn't find the order you're looking for.</p>
        <Button onClick={() => navigate('/orders')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Button>
      </div>
    );
  }

  const isCancellable = order?.status === 'pending' || order?.status === 'processing';

  return (
    <>
      <SEO title={`Order #${order.order_number || orderId?.slice(0, 8)}`} />
      <div className="container mx-auto max-w-5xl py-8 px-4 sm:px-6 lg:px-8">
        <BackButton to="/orders" label="Back to My Orders" useSmartNavigation />
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-3">
            <Package className="h-8 w-8" />
            Order #{order.order_number}
          </h1>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate(`/invoice/${order.id}`)} 
              className="bg-transparent hover:bg-white/10"
            >
              <Receipt className="mr-2 h-4 w-4" />
              View Invoice
            </Button>
            <Button variant="outline" onClick={() => navigate('/orders')} className="bg-transparent hover:bg-white/10">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Orders
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="text-muted-foreground block">Status</span>
                  <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                </div>
                <div>
                  <span className="text-muted-foreground block">Payment</span>
                  <Badge className={getPaymentStatusColor(order.payment_status)}>{order.payment_status}</Badge>
                </div>
                <div>
                  <span className="text-muted-foreground block">Total</span>
                  <span className="font-semibold text-lg">${Number(order.total_amount).toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Date</span>
                  <span>{format(new Date(order.created_at), 'MMM dd, yyyy')}</span>
                </div>
              </div>
              {order.tracking_number && (
                <div className="p-3 bg-blue-500/10 rounded-lg mt-4">
                  <p className="text-sm font-medium text-blue-300">Tracking Number</p>
                  <p className="font-mono text-blue-200">{order.tracking_number}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card variant="glass">
            <CardHeader>
              <CardTitle>Items ({order.order_items?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.order_items?.map((item: any) => {
                  const OrderItemWithReview = () => {
                    const { data: hasReviewed } = useHasUserReviewed(item.product_id || '');
                    const isDelivered = order.status === 'delivered';

                    return (
                      <div key={item.id} className="flex items-center gap-4 p-4 border border-white/10 rounded-lg">
                        <div className="w-16 h-16 bg-white/5 rounded-lg flex items-center justify-center shrink-0">
                          {item.products?.image_url ? (
                            <img src={item.products.image_url} alt={item.products.name} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Package className="h-6 w-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{item.products?.name}</h4>
                          <p className="text-sm text-muted-foreground">Quantity: {item.quantity} × ${Number(item.price).toFixed(2)}</p>
                          {isDelivered && !hasReviewed && (
                            <Button
                              variant="link"
                              size="sm"
                              className="mt-2 p-0 h-auto text-primary hover:text-primary/80"
                              onClick={() => {
                                setReviewProductId(item.product_id || '');
                                setReviewProductName(item.products?.name || '');
                              }}
                            >
                              <Star className="mr-1 h-3 w-3" />
                              Write a review
                            </Button>
                          )}
                          {hasReviewed && (
                            <p className="text-xs text-muted-foreground mt-2">✓ Reviewed</p>
                          )}
                        </div>
                        <div className="text-right font-medium">${(Number(item.price) * item.quantity).toFixed(2)}</div>
                      </div>
                    );
                  };

                  return <OrderItemWithReview key={item.id} />;
                })}
              </div>
            </CardContent>
          </Card>

          {order.shipping_address && (
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm">{renderShippingAddress(order.shipping_address)}</div>
              </CardContent>
            </Card>
          )}

          {isCancellable && (
            <div className="flex justify-end gap-2 pt-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={isCancelling}>
                    {isCancelling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-2 h-4 w-4" />}
                    Cancel Order
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>This action cannot be undone. This will permanently cancel your order.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Go back</AlertDialogCancel>
                    <AlertDialogAction onClick={handleCancelOrder} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Yes, cancel order</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </div>

      {/* Write Review Dialog */}
      {reviewProductId && (
        <WriteReviewDialog
          open={!!reviewProductId}
          onOpenChange={(open) => {
            if (!open) {
              setReviewProductId(null);
              setReviewProductName('');
            }
          }}
          productId={reviewProductId}
          productName={reviewProductName}
        />
      )}
    </>
  );
};

export default OrderDetailPage;
